import { chatAvatarPerson } from '../../utils/employeeAvatar';
import { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import CircularProgress from '@mui/joy/CircularProgress';

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { APIGetEmployeesNotInChat, APIAddUserToChat } from '../../api';
import { LayoutContext } from '../../context';
import { presenceSubtitle } from '../../hooks/usePresence';
import { isDirectChat } from './directChat';
import ModalChatInfo from '../Modals/ModalChatInfo';
import AvatarWithStatus from './AvatarWithStatus';
import { CHAT, MOTION, ellipsis, participantsLabel, iconColor } from './chatTheme';

/**
 * Шапка открытого чата.
 *
 * Что изменилось: вместо серого пустого кружка и строки «ID чата: 123»
 * — аватар с инициалами и цветом собеседника, название с эллипсисом и
 * осмысленный подзаголовок (число участников и состояние соединения).
 * Клик по шапке открывает карточку чата — так ожидается в мессенджерах.
 */
export default function MessagesPaneHeader({
  chat, wsConnected, wsFatalCode, presence = null,
  participants = [], setSelectedChat, searchParams, setSearchParams, onUserAdded,
}) {
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [chatInfoOpen, setChatInfoOpen] = useState(false);

  const title = chat?.display_name || chat?.name || 'Чат';
  const count = participants.length;
  const direct = isDirectChat(chat);

  // Точка = человек. В личке — собеседник, в группе — «есть ли хоть кто-то».
  const peerOnline = !direct && presence?.is_group
    ? (presence?.online_count || 0) > 0
    : !!presence?.peer?.online;

  // Подпись = живой статус: «в сети» / «был(а) вчера в 14:30» / «Аня и ещё 3 в сети».
  const subtitle = direct
    ? presence?.peer?.last_seen_text || null
    : presenceSubtitle(presence);

  // Состояние связи — это про НАС, а не про собеседника, поэтому оно ушло из
  // точки в подпись и показывается только когда реально мешает.
  const connectionNote = wsFatalCode === 4003
    ? 'нет доступа к чату'
    : wsFatalCode === 4001
      ? 'сессия истекла, войдите заново'
      : (!wsConnected ? 'соединение восстанавливается…' : null);

  return (
    <Box
      component={motion.header}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: MOTION.base, ease: MOTION.ease }}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        // Высота как в мессенджерах: полоса не должна отъедать полотно.
        // Было 64px плюс вертикальные отступы — в сумме под 80px.
        minHeight: CHAT.headerHeight,
        // Шапка — край колонки: её нельзя ни сжать, ни утащить вместе с
        // лентой. Прокручивается только список сообщений между ними.
        flexShrink: 0,
        px: { xs: 1, md: 1.75 },
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(14px)',
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(20,24,35,0.78)'
          : 'rgba(255,255,255,0.82)',
        position: 'relative',
        zIndex: 4,
      })}
    >
      <Stack
        direction="row"
        spacing={1.25}
        sx={{ alignItems: 'center', minWidth: 0, flex: 1, cursor: 'pointer' }}
        onClick={() => setChatInfoOpen(true)}
      >
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedChat(null);
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('chatId');
            setSearchParams(newParams);
            // openMessagesPane() здесь больше нет: он ставил
            // document.body.style.overflow='hidden', и после возврата к списку
            // страница на телефоне переставала прокручиваться и нажиматься.
            // Показывать список отдельно не нужно — он и так под модалкой.
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 17 }} />
        </IconButton>

        {/* Точка теперь про ЧЕЛОВЕКА, а не про сокет.
            Раньше она горела зелёным, пока жив веб-сокет, — и все читали её
            как «собеседник в сети», потому что именно это она означает в
            любом мессенджере. Состояние связи ушло в подпись, где ему и
            место: это про нас, а не про него. */}
        <AvatarWithStatus name={title} person={chatAvatarPerson({ ...chat, participants })} size="sm" showStatus online={peerOnline} />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            level="title-sm"
            sx={{ ...ellipsis, fontWeight: 700, fontSize: 14.5, lineHeight: 1.25 }}
          >
            {title}
            {direct && <Box component="span" sx={{ ml: 0.75, fontSize: 10, fontWeight: 800,
              px: 0.6, py: 0.15, borderRadius: '6px', color: 'primary.600', bgcolor: 'primary.softBg',
              verticalAlign: 'middle' }}>ЛС</Box>}
          </Typography>
          <Typography
            level="body-xs"
            sx={{
              ...ellipsis, fontSize: 11.5, lineHeight: 1.25,
              color: connectionNote ? 'warning.600' : (peerOnline ? 'success.600' : 'text.tertiary'),
              fontWeight: peerOnline ? 600 : 400,
              transition: 'color .2s',
            }}
          >
            {/* Приоритет подписи: сначала то, что мешает работать (обрыв,
                отказ), потом живые статусы, и только потом статика вроде
                «Чат №12» — иначе важное прячется за неважным. */}
            {connectionNote || subtitle || (direct ? 'Личный диалог' : count > 0 ? participantsLabel(count) : `Чат №${chat?.chat_id}`)}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={0.5} direction="row" sx={{ alignItems: 'center', flexShrink: 0 }}>
        {!direct && <AddContactDropdown
          chat={chat}
          open={addContactOpen}
          onClose={() => setAddContactOpen(false)}
          setAddContactOpen={setAddContactOpen}
          onUserAdded={onUserAdded}
        />}

        <Tooltip title="Информация о чате" size="sm">
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => setChatInfoOpen(true)}
            sx={(theme) => ({
              borderRadius: '50%',
              // Через --Icon-color, иначе Joy оставляет иконку почти чёрной
              ...iconColor(theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)'),
              '&:hover': {
                backgroundColor: 'background.level1',
                ...iconColor('var(--joy-palette-primary-500)'),
              },
            })}
          >
            <MoreVertRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <ModalChatInfo
        open={chatInfoOpen}
        onClose={() => setChatInfoOpen(false)}
        chatId={chat?.chat_id}
        chat={chat}
        onParticipantsChanged={onUserAdded}
        presence={presence}
      />
    </Box>
  );
}

function AddContactDropdown({ chat, open, onClose, setAddContactOpen, onUserAdded }) {
  const { tokenEmployee } = useContext(LayoutContext);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && chat) loadEmployees();
  }, [open, chat]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await APIGetEmployeesNotInChat(chat.chat_id, tokenEmployee);
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Ошибка загрузки исполнителей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (employeeId) => {
    try {
      setLoading(true);
      const response = await APIAddUserToChat(chat.chat_id, employeeId, tokenEmployee);
      if (response.status === 'Пользователь успешно добавлен в чат') {
        onUserAdded?.();
        onClose();
      }
    } catch (error) {
      console.error('Ошибка добавления пользователя:', error);
      alert('Ошибка при добавлении пользователя в чат');
    } finally {
      setLoading(false);
    }
  };

  function GetPosition(position) {
    if (position?.includes('manager')) return 'Менеджер';
    if (position?.includes('surveyor')) return 'Обмерщик';
    if (position?.includes('draftsman')) return 'Чертежник';
    if (position?.includes('quality_master')) return 'Мастер качества';
    return '—';
  }

  const filteredEmployees = employees.filter((employee) => {
    const q = searchTerm.toLowerCase();
    const full = `${employee.last_name || ''} ${employee.name || ''}`.toLowerCase();
    return full.includes(q)
      || (Array.isArray(employee.position) && employee.position.some((p) => p.toLowerCase().includes(q)));
  });

  return (
    <Tooltip
      placement="bottom-end"
      variant="plain"
      open={open}
      sx={{ p: 0, background: 'transparent', boxShadow: 'none', zIndex: 10020 }}
      title={(
        <Sheet
          component={motion.div}
          initial={{ opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: MOTION.fast, ease: MOTION.ease }}
          variant="outlined"
          sx={{ borderRadius: '18px', p: 1.5, boxShadow: 'lg', width: 320 }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography level="title-sm" sx={{ fontWeight: 700 }}>Добавить участника</Typography>
            <IconButton size="sm" variant="plain" onClick={onClose} sx={{ borderRadius: '50%' }}>
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Input
            size="sm"
            placeholder="Поиск сотрудника…"
            startDecorator={<SearchRoundedIcon sx={{ fontSize: 17 }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1, borderRadius: '12px' }}
          />

          {loading ? (
            <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size="sm" /></Stack>
          ) : (
            <List sx={{ maxHeight: 280, overflow: 'auto', '--List-gap': '2px', p: 0 }}>
              {filteredEmployees.map((employee) => (
                <Box
                  key={employee.id}
                  onClick={() => !loading && handleAddUser(employee.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 1, py: 0.75, borderRadius: '12px', cursor: 'pointer',
                    transition: 'background-color .15s',
                    '&:hover': { backgroundColor: 'background.level1' },
                  }}
                >
                  <AvatarWithStatus
                    size="sm"
                    name={`${employee.last_name || ''} ${employee.name || ''}`}
                    person={employee}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-sm" sx={{ ...ellipsis, fontWeight: 600 }}>
                      {employee?.last_name} {employee.name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {GetPosition(employee.position)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </List>
          )}

          {filteredEmployees.length === 0 && !loading && (
            <Typography level="body-xs" sx={{ textAlign: 'center', py: 2, color: 'text.tertiary' }}>
              Нет доступных сотрудников
            </Typography>
          )}
        </Sheet>
      )}
    >
      <IconButton
        size="sm"
        variant="plain"
        color="neutral"
        onClick={() => setAddContactOpen(!open)}
        sx={(theme) => ({
          borderRadius: '50%',
          ...iconColor(theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)'),
          '&:hover': {
            backgroundColor: 'background.level1',
            ...iconColor('var(--joy-palette-primary-500)'),
          },
        })}
      >
        <PersonAddAltRoundedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  );
}
